import styled from '@emotion/styled'
import { colors, spacing, font, radius, shadow } from '@mobi/component-library/Theme/Common'

type Props = { invalid?: boolean; disabled?: boolean; isPlaceholderSelected?: boolean }

function determineColors(isDisabled: boolean, isInvalid: boolean) {
  if (isDisabled) {
    return {
      background: colors.surface[100],
      border: colors.neutral[400],
      helpText: colors.neutral[400],
    }
  }

  if (isInvalid) {
    return {
      background: colors.error[50],
      border: colors.error[600],
      helpText: colors.error[600],
    }
  }

  return {
    background: colors.white,
    border: colors.neutral[300],
    helpText: colors.neutral[600],
  }
}

function getSelectColor(disabled: boolean, isPlaceholderSelected: boolean) {
  if (disabled) {
    return colors.neutral[400]
  }
  if (isPlaceholderSelected) {
    return colors.neutral[500]
  }
  return colors.black
}

export const DropdownInputStyled = styled.div<Props>(
  ({ invalid = false, disabled = false, isPlaceholderSelected = false }) => {
    const effectiveColors = determineColors(disabled, invalid)

    return {
      color: disabled ? colors.neutral[600] : colors.black,
      fontFamily: font.family.primary,
      ...font.size.lg,

      '& > label': {
        display: 'block',
        fontWeight: font.weight.medium,
        ...font.size.lg,
        marginBottom: spacing.smx2,
      },

      '& > div': {
        alignItems: 'center',
        backgroundColor: effectiveColors.background,
        border: `1px solid ${effectiveColors.border}`,
        borderRadius: radius.lgx1,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: shadow.xs,
        height: '4.4rem',
        outlineColor: 'transparent',
        outlineStyle: 'solid',
        outlineWidth: '4px',

        '&:focus-within': {
          borderColor: colors.lavender[300],
          outlineColor: colors.lavender[50],
        },

        select: {
          appearance: 'none',
          '-webkit-appearance': 'none',
          '-moz-appearance': 'none',
          width: '100%',
          background: 'none',
          border: 'none',
          caretColor: colors.studio[500],
          color: getSelectColor(disabled, isPlaceholderSelected),
          flex: '1 1 auto',
          font: 'inherit',
          fontSize: 'inherit',
          fontWeight: font.weight.regular,
          height: '100%',
          letterSpacing: 'inherit',
          lineHeight: 'inherit',

          '&:not(:first-child)': {
            marginInlineStart: spacing.sm,

            '@supports not (margin-inline-start: 0)': { marginLeft: spacing.md },
          },

          '&:focus': { outline: 'none' },

          option: {
            color: colors.black,
          },
        },
      },

      '& > div > :first-child': {
        paddingLeft: spacing.md,
      },

      '& > span': {
        color: effectiveColors.helpText,
        display: 'block',
        marginTop: spacing.smx1,
        ...font.size.md,

        '&:empty': { display: 'none' },
      },
    }
  }
)

export const DropdownChevron = styled.div({
  span: {
    position: 'absolute',
    pointerEvents: 'none',
    right: spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
  },
})
